import { useState } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { whatsappService } from '../services/whatsappService';

export const useReservationPDFShare = () => {
    const [loading, setLoading] = useState(false);
    const sanitizeFileName = (value) => {
        const normalized = String(value || 'client')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');

        return normalized.replace(/[^a-zA-Z0-9_-]/g, '_');
    };

    const waitForRender = () =>
        new Promise((resolve) => {
            requestAnimationFrame(() => requestAnimationFrame(resolve));
        });

    const waitForElement = (elementId, timeoutMs = 3000) =>
        new Promise((resolve, reject) => {
            const start = Date.now();

            const check = () => {
                const element = document.getElementById(elementId);
                if (element) {
                    resolve(element);
                    return;
                }

                if (Date.now() - start >= timeoutMs) {
                    reject(new Error(`Élément "${elementId}" non trouvé`));
                    return;
                }

                requestAnimationFrame(check);
            };

            check();
        });

    const buildReservationMessage = (reservation, clientName) => `
Bonjour ${clientName},

✅ Votre reservation est confirmee.

📅 Date: ${new Date(reservation.date).toLocaleDateString('fr-FR')}
⏰ Heure: ${reservation.time}
💇 Service: ${reservation.serviceName}
💰 Prix: ${reservation.price}€
📍 Salon: OV HAIRSTYLE

Nous restons disponibles si vous avez des questions.
Merci pour votre confiance.
    `.trim();

    const generateReservationPDFFile = async (clientName) => {
        const pdfElement = await waitForElement('reservation-pdf');

        // Laisse le temps au DOM caché de se rendre avant capture
        await waitForRender();

        const safeClientName = sanitizeFileName(clientName);
        const fileName = `Confirmation_${safeClientName}_${new Date().getTime()}.pdf`;

        const canvas = await html2canvas(pdfElement, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
        });

        const pdf = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
        });

        const imgData = canvas.toDataURL('image/png');
        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

        const pdfBlob = pdf.output('blob');
        if (!pdfBlob || pdfBlob.size === 0) {
            throw new Error('Impossible de generer le PDF');
        }

        const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

        return { fileName, pdfBlob, pdfFile };
    };

    const openConfirmationWhatsApp = async (reservation, phoneNumber, clientName, existingWindow = null) => {
        try {
            setLoading(true);

            const message = buildReservationMessage(reservation, clientName);
            whatsappService.openWhatsApp(phoneNumber, message, existingWindow);

            return true;
        } catch (error) {
            console.error('[useReservationPDFShare] Erreur:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const shareReservationPDF = async (reservation, clientName) => {
        try {
            setLoading(true);

            const { fileName, pdfBlob, pdfFile } = await generateReservationPDFFile(clientName);
            const shareText = `Confirmation de reservation - ${clientName}`;
            const downloadFile = () => {
                const objectUrl = URL.createObjectURL(pdfBlob);
                const link = document.createElement('a');
                link.href = objectUrl;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(objectUrl);
            };

            if (navigator.share) {
                const canShareFiles = typeof navigator.canShare === 'function'
                    ? navigator.canShare({ files: [pdfFile] })
                    : true;

                if (canShareFiles) {
                    try {
                        await navigator.share({
                            title: 'Confirmation de reservation',
                            text: shareText,
                            files: [pdfFile],
                        });
                        return { shared: true, downloaded: false, cancelled: false };
                    } catch (shareError) {
                        if (shareError?.name === 'AbortError') {
                            return { shared: false, downloaded: false, cancelled: true };
                        }
                    }
                }
            }

            downloadFile();

            return { shared: false, downloaded: true, cancelled: false };
        } catch (error) {
            console.error('[useReservationPDFShare] Erreur partage PDF:', error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    return {
        openConfirmationWhatsApp,
        shareReservationPDF,
        loading,
    };
};
